import { ConferenceStreamType, EndpointState, ConferenceEventType, } from "./model";
export var ConferenceEventProcessingErrorType;
(function (ConferenceEventProcessingErrorType) {
    ConferenceEventProcessingErrorType["OLD_VERSION"] = "OLD_VERSION";
    ConferenceEventProcessingErrorType["FUTURE_VERSION"] = "FUTURE_VERSION";
    ConferenceEventProcessingErrorType["MISSING_VERSION"] = "MISSING_VERSION";
})(ConferenceEventProcessingErrorType || (ConferenceEventProcessingErrorType = {}));
export class ConferenceEventProcessingError extends Error {
    constructor(type, event, message) {
        super(message);
        this.type = type;
        this.event = event;
    }
}
function findParticipantById(state, id) {
    const matchedParticipants = state.participants.filter((it) => it.id === id);
    if (matchedParticipants.length === 1) {
        return matchedParticipants[0];
    }
    if (matchedParticipants.length === 0) {
        return null;
    }
    throw new Error(`Smells like a bug! Duplicate participant entry ${id} found!`);
}
function findEndpointByParticipantIdAndEndpointId(state, participantId, endpointId) {
    const participant = findParticipantById(state, participantId);
    if (!participant) {
        throw new Error(`participant ${participantId} not found!`);
    }
    const matchedEndpoints = participant.endpoints.filter((it) => it.id === endpointId);
    if (matchedEndpoints.length === 1) {
        return matchedEndpoints[0];
    }
    if (matchedEndpoints.length === 0) {
        return null;
    }
    throw new Error("Smells like a bug! Duplicate endpoint entry found: " +
        `{userId: ${participantId}, userEndpointId: ${endpointId}}`);
}
function addEndpoint(state, userId, userEndpointId, metadata) {
    const endpoint = {
        id: userEndpointId,
        state: EndpointState.HEALTHY,
        // because the processor only handles "live" events,
        // and a new endpoint will always be healthy
        streams: [],
        metadata,
    };
    let participant = findParticipantById(state, userId);
    if (!participant) {
        participant = {
            id: userId,
            endpoints: [endpoint],
        };
        state.participants.push(participant);
    }
    else {
        participant.endpoints.push(endpoint);
    }
}
function removeEndpoint(state, userId, userEndpointId) {
    const participant = findParticipantById(state, userId);
    if (!participant) {
        throw new Error(`participant ${userId} not found!`);
    }
    else {
        participant.endpoints = participant.endpoints.filter((it) => it.id !== userEndpointId);
        if (participant.endpoints.length === 0) {
            state.participants = state.participants.filter((it) => it.id !== userId);
        }
    }
}
function addStream(state, userId, userEndpointId, streamId, streamType, metadata) {
    const endpoint = findEndpointByParticipantIdAndEndpointId(state, userId, userEndpointId);
    if (!endpoint) {
        throw new Error(`endpoint { userId: ${userId}, endpointId: ${userEndpointId} }` +
            " not found");
    }
    if (endpoint.streams.filter((it) => it.id === streamId).length > 0) {
        throw new Error(`stream from user ${userId}, endpoint ${userEndpointId} and streamId` +
            ` ${streamId} was found`);
    }
    endpoint.streams.push({
        id: streamId,
        type: ConferenceStreamType[streamType],
        metadata,
    });
}
function removeStream(state, userId, userEndpointId, streamId) {
    const endpoint = findEndpointByParticipantIdAndEndpointId(state, userId, userEndpointId);
    if (!endpoint) {
        throw new Error(`endpoint with userId: ${userId} and userEndpointId: ` +
            `${userEndpointId} not found`);
    }
    endpoint.streams = endpoint.streams.filter((it) => it.id !== streamId);
}
function updateEndpointState(state, userId, userEndpointId, userEndpointState) {
    const endpoint = findEndpointByParticipantIdAndEndpointId(state, userId, userEndpointId);
    if (!endpoint) {
        throw new Error(`endpoint with userId: ${userId} and userEndpointId: ` +
            `${userEndpointId} not found`);
    }
    endpoint.state = userEndpointState;
}
function updateMetadata(state, event) {
    if (!state.metadata) {
        state.metadata = {};
    }
    if (event.operation === "SET") {
        state.metadata[event.key] = {
            lastModified: event.timestamp,
            userEndpointId: event.userEndpointId,
            userId: event.userId,
            value: event.value,
        };
    }
    else if (event.operation === "REMOVE") {
        state === null || state === void 0 ? true : delete state.metadata[event.key];
    }
    else {
        throw new Error(`unknown operation ${event.operation}, probably a bug here`);
    }
}
function updateParticipantMetadata(state, event) {
    const userId = event.userId;
    const userEndpointId = event.userEndpointId;
    const newMetadata = event.newMetadata;
    const endpoint = findEndpointByParticipantIdAndEndpointId(state, userId, userEndpointId);
    if (!endpoint) {
        throw new Error(`endpoint with userId: ${userId} and userEndpointId: ` +
            `${userEndpointId} not found`);
    }
    endpoint.metadata = newMetadata;
}
function updateStreamMetadata(state, event) {
    const userId = event.userId;
    const userEndpointId = event.userEndpointId;
    const streamId = event.streamId;
    const newMetadata = event.newMetadata;
    const endpoint = findEndpointByParticipantIdAndEndpointId(state, userId, userEndpointId);
    if (!endpoint) {
        throw new Error(`endpoint with userId: ${userId} and userEndpointId: ` +
            `${userEndpointId} not found`);
    }
    const stream = endpoint.streams.find((it) => it.id === streamId);
    if (!stream) {
        throw new Error(`stream with id ${streamId}, userId: ${userId} and userEndpointId: ` +
            `${userEndpointId} not found`);
    }
    stream.metadata = newMetadata;
}
export class ConferenceEventProcessor {
    static processEvent(state, event) {
        if (!Number.isInteger(event.conferenceVersion)) {
            const message = "received event with no conferenceVersion";
            throw new ConferenceEventProcessingError(ConferenceEventProcessingErrorType.MISSING_VERSION, event, message);
        }
        if (event.conferenceVersion < state.version) {
            const message = `received an older event. our version is ${state.version}` +
                ` event version is ${event.conferenceVersion}.You should ignore this` +
                " event, as it is possibly a duplicate delivery";
            throw new ConferenceEventProcessingError(ConferenceEventProcessingErrorType.OLD_VERSION, event, message);
        }
        if (event.conferenceVersion > state.version) {
            const message = `received a future of order event. our version is ${state.version}` +
                ` event version is ${event.conferenceVersion}. You should retry processing` +
                " this event at a later time.";
            throw new ConferenceEventProcessingError(ConferenceEventProcessingErrorType.FUTURE_VERSION, event, message);
        }
        switch (event.type) {
            case ConferenceEventType.CONFERENCE_JOINED:
                addEndpoint(state, event.userId, event.userEndpointId, event.metadata);
                break;
            case ConferenceEventType.CONFERENCE_LEFT:
                removeEndpoint(state, event.userId, event.userEndpointId);
                break;
            case ConferenceEventType.CONFERENCE_ENDED:
                try {
                    removeEndpoint(state, event.userId, event.userEndpointId);
                }
                catch (ignored) {
                    return;
                }
                break;
            case ConferenceEventType.CONFERENCE_STREAM_PUBLISHED:
                addStream(state, event.userId, event.userEndpointId, event.streamId, event.streamType, event.metadata);
                break;
            case ConferenceEventType.CONFERENCE_STREAM_UNPUBLISHED:
                removeStream(state, event.userId, event.userEndpointId, event.streamId);
                break;
            case ConferenceEventType.CONFERENCE_PARTICIPANT_STATE_CHANGED_EVENT:
                updateEndpointState(state, event.userId, event.userEndpointId, event.state);
                break;
            case ConferenceEventType.CONFERENCE_METADATA_UPDATE:
                updateMetadata(state, event);
                break;
            case ConferenceEventType.CONFERENCE_PARTICIPANT_METADATA_UPDATED:
                updateParticipantMetadata(state, event);
                break;
            case ConferenceEventType.CONFERENCE_STREAM_METADATA_UPDATED:
                updateStreamMetadata(state, event);
                break;
        }
        // update our conferenceVersion
        state.version++;
    }
}
